import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

/**
 * Workshop: Billing section for Settings.
 * Shows current plan, usage, payment method, and invoice history.
 */

const MOCK_PLAN = {
  name: "Pro",
  price: "$29/mo",
  status: "active" as const,
  renewalDate: "April 11, 2026",
  tokensUsed: 847_000,
  tokensLimit: 1_000_000,
};

const MOCK_PAYMENT = {
  brand: "Visa",
  last4: "4242",
  expiry: "12/27",
};

const MOCK_INVOICES = [
  { id: "inv_1", date: "Mar 1, 2026", amount: "$29.00", status: "paid" },
  { id: "inv_2", date: "Feb 1, 2026", amount: "$29.00", status: "paid" },
  { id: "inv_3", date: "Jan 1, 2026", amount: "$29.00", status: "paid" },
];

function formatTokens(value: number): string {
  return `${Math.round(value / 1000)}k`;
}

interface BillingSectionProps {
  className?: string;
  "data-testid"?: string;
}

export function BillingSection({
  className,
  "data-testid": testId = "settings-billing",
}: BillingSectionProps) {
  const usagePercent = Math.round(
    (MOCK_PLAN.tokensUsed / MOCK_PLAN.tokensLimit) * 100,
  );

  return (
    <div className={cn("flex flex-col gap-4", className)} data-testid={testId}>
      {/* Current plan */}
      <div
        className="rounded-lg border border-glass-stroke-subtle p-4"
        data-testid="billing-plan"
      >
        <p className="mb-2 text-[10px] font-medium text-secondary">
          CURRENT PLAN
        </p>
        <div className="flex items-center gap-3">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-primary">
                {MOCK_PLAN.name}
              </span>
              <Badge variant="default" data-testid="billing-plan-badge">
                {MOCK_PLAN.status}
              </Badge>
            </div>
            <span className="text-[11px] text-primary">
              {MOCK_PLAN.price} &middot; Renews {MOCK_PLAN.renewalDate}
            </span>
          </div>
          <Button
            variant="secondary"
            size="sm"
            data-testid="billing-change-plan"
          >
            Change plan
          </Button>
        </div>
      </div>

      {/* Usage */}
      <div
        className="rounded-lg border border-glass-stroke-subtle p-4"
        data-testid="billing-usage"
      >
        <p className="mb-2 text-[10px] font-medium text-secondary">USAGE</p>
        <div className="flex flex-col gap-2">
          <div
            className="h-2 w-full overflow-hidden rounded-full bg-glass-fill-heavy"
            data-testid="billing-usage-bar"
          >
            <div
              className="h-full rounded-full bg-accent transition-all"
              style={{ width: `${usagePercent}%` }}
            />
          </div>
          <span className="text-[11px] text-primary">
            {formatTokens(MOCK_PLAN.tokensUsed)} /{" "}
            {formatTokens(MOCK_PLAN.tokensLimit)} tokens ({usagePercent}%)
          </span>
        </div>
      </div>

      {/* Payment method */}
      <div
        className="flex items-center gap-3 rounded-lg border border-glass-stroke-subtle p-3"
        data-testid="billing-payment"
      >
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-btn-ghost-fill text-secondary">
          <Icons.CreditCard className="h-4 w-4" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-[11px] font-medium text-primary">
            {MOCK_PAYMENT.brand} ending in {MOCK_PAYMENT.last4}
          </span>
          <span className="text-[10px] text-secondary">
            Exp {MOCK_PAYMENT.expiry}
          </span>
        </div>
        <Button variant="ghost" size="sm" data-testid="billing-update-payment">
          Update
        </Button>
      </div>

      {/* Invoices */}
      <div data-testid="billing-invoices">
        <p className="mb-2 text-[10px] font-medium text-secondary">INVOICES</p>
        <div className="flex flex-col gap-1">
          {MOCK_INVOICES.map((invoice) => (
            <div
              key={invoice.id}
              className="flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-btn-ghost-fill"
              data-testid={`billing-invoice-${invoice.id}`}
            >
              <span className="flex-1 text-[11px] text-primary">
                {invoice.date}
              </span>
              <span className="text-[11px] text-secondary">
                {invoice.amount}
              </span>
              <Button
                variant="ghost"
                size="icon"
                data-testid={`billing-download-${invoice.id}`}
              >
                <Icons.Download className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export type { BillingSectionProps };
